import { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle, Gift } from 'lucide-react';
import { SignupFormData } from '../types';
import { FOUNDING_MEMBER_PRICE, ANNUAL_PRICES } from '../constants';

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  interval?: 'monthly' | 'annual';
}

export default function SignupModal({ isOpen, onClose, interval = 'monthly' }: SignupModalProps) {
  const [form, setForm] = useState<SignupFormData>({ email: '', name: '', gymName: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referralCode, setReferralCode] = useState<string | null>(null);

  useEffect(() => {
    const ref = localStorage.getItem('athnx_ref');
    if (ref) setReferralCode(ref);
  }, []);

  if (!isOpen) return null;

  const isAnnual = interval === 'annual';
  const annualTotal = ANNUAL_PRICES.iron.foundingAnnual || 490;
  const displayPrice = isAnnual ? `$${annualTotal}/year` : `$${FOUNDING_MEMBER_PRICE}/month`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/public/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, interval, ...(referralCode ? { referralCode } : {}) }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Signup failed');
      }

      // Redirect to Stripe Checkout
      if (data.url) {
        localStorage.removeItem('athnx_ref');
        window.location.href = data.url;
        return;
      }

      setSuccess(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#161b26] p-8 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {success ? (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">You're on the list!</h3>
            <p className="text-white/60">
              We'll reach out with your founding member access within 24 hours.
            </p>
          </div>
        ) : (
          <>
            {referralCode && (
              <div className="flex items-center gap-2 px-3 py-2 mb-3 rounded-lg border border-green-500/30 bg-green-500/10 text-green-400 text-xs font-medium">
                <Gift className="w-4 h-4 flex-shrink-0" />
                <span>Referred by a friend — 50% off your first month!</span>
              </div>
            )}
            <h3 className="text-2xl font-bold mb-2">
              Become a Founding Member
            </h3>
            <p className="text-white/60 text-sm mb-1">
              {displayPrice}, locked for life. Limited to 10 spots.
            </p>
            {isAnnual && (
              <p className="text-green-400 text-xs mb-4">
                Annual billing — 2 months free (${Math.round(annualTotal / 12)}/mo effective)
              </p>
            )}
            {!isAnnual && <div className="mb-6" />}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  Your Name
                </label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="you@yourgym.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  Gym Name
                </label>
                <input
                  type="text"
                  required
                  value={form.gymName}
                  onChange={(e) => setForm(prev => ({ ...prev, gymName: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-blue-500 transition-colors"
                  placeholder="Iron City Fitness"
                />
              </div>

              {error && (
                <p className="text-red-400 text-sm">{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 text-white font-bold transition-all duration-200 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Joining...
                  </>
                ) : (
                  `Join — ${displayPrice} for life`
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
